import { Settings}  from "../../Settings";
import Vector from "../math/Vector";
import GridRegion from "./GridRegion";
import GridTile from "./GridTile";
import Grid from "./Grid";
import Fast from "../../util/Fast";
import GameModel from "../../GameModel";

export default class WorldGrid extends GameModel { 
    /**
     */
    constructor() {
        super();

        /**
         * @constant
         * @type {Vector}
         */
        this.origin = new Vector(0, 0);

        /**
         * @constant
         * @type {Array.<Grid>}
         */
        this.grids = this.instantiateGrids();

        // One-time job of finding all the tile neighbors in the grids.
        for (let i = 0; i < this.grids.length; i++) {
            this.grids[i].findTileNeighborsFull();
        }

        /**
         * Incremented any time a grid is loaded/unloaded.
         * Also updated whenever a grid is changed (tile is mined)
         * @type {number}
         */
        this.changeCount = 0;
        /**
         * Incremented any time the world shifts to a new center grid.
         * @type {number}
         */
        this.shiftCount = 0;
    }

    /**
     * @private
     * @returns {Grid[]}
     */
    instantiateGrids() {
        const c = Settings.grid.gridPixelWidth;
        const r = Settings.grid.gridPixelHeight;

        const grids = new Array(9);

        // Top Row
        grids[0] = new Grid(this, new Vector(-c, -r)); // NORTH WEST
        grids[1] = new Grid(this, new Vector(0, -r)); // NORTH
        grids[2] = new Grid(this, new Vector(c, -r)); // NORTH EAST

        // Middle Row
        grids[3] = new Grid(this, new Vector(-c, 0)); // WEST
        grids[4] = new Grid(this, new Vector(0, 0)); // CENTER
        grids[5] = new Grid(this, new Vector(c, 0)); // EAST

        // Bottom Row
        grids[6] = new Grid(this, new Vector(-c, r)); // SOUTH WEST
        grids[7] = new Grid(this, new Vector(0, r)); // SOUTH
        grids[8] = new Grid(this, new Vector(c, r)); // SOUTH EAST

        return grids;
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTile(tileCol, tileRow) {

        const grid = this.getGrid(tileCol, tileRow);
        if (!grid) {
            return null;
        }

        return grid.getGridTile(tileCol, tileRow);
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {Grid}
     */
    getGrid(tileCol, tileRow) {
        const x = tileCol * Settings.tile.size;
        const y = tileRow * Settings.tile.size;

        const centerGrid = this.grids[4];
        if (!centerGrid) {
            return null;
        }

        if (centerGrid.containsXY(x, y)) {
            return centerGrid;
        }

        const centerLeftX = centerGrid.origin.x;
        const centerRightX = centerLeftX + Settings.grid.gridPixelWidth;

        const centerTopY = centerGrid.origin.y;
        const centerBottomY = centerTopY + Settings.grid.gridPixelHeight;

        // [0][1][2]
        // [3][4][5]
        // [6][7][8]

        let candidate = null;
        if (x >= centerLeftX && x < centerRightX) {
            if (y < centerTopY) {
                candidate = this.grids[1]; // top center
            }
            else if (y >= centerBottomY) {
                candidate = this.grids[7]; // bottom center
            }
        }
        else if (x < centerGrid.origin.x) {
            if (y >= centerTopY && y < centerBottomY) {
                candidate = this.grids[3]; // left
            }
            else if (y < centerTopY) {
                candidate = this.grids[0]; // top left
            }
            else if (y >= centerBottomY) {
                candidate = this.grids[6]; // bottom left
            }
        }
        else if (x >= centerRightX) {
            if (y >= centerTopY && y < centerBottomY) {
                candidate = this.grids[5]; // right
            }
            else if (y < centerTopY) {
                candidate = this.grids[2]; // top right
            }
            else if (y >= centerBottomY) {
                candidate = this.grids[8]; // bottom right
            }
        }

        if (candidate && candidate.containsXY(x, y)) {
            return candidate;            
        }

        return null;
    }

    /**
     * @param {Vector} pixelLocation 
     * @returns {Grid}
     */
    findGridContaining(pixelLocation) {
        const tileCol = Fast.floor(pixelLocation.x / Settings.tile.size);
        const tileRow = Fast.floor(pixelLocation.y / Settings.tile.size);

        return this.getGrid(tileCol, tileRow);
    }

    /**
     * @param {Vector} pixelLocation 
     * @returns {GridTile}
     */
    findGridTile(pixelLocation) {
        const tileCol = Fast.floor(pixelLocation.x / Settings.tile.size);
        const tileRow = Fast.floor(pixelLocation.y / Settings.tile.size);

        return this.getGridTile(tileCol, tileRow);
    }

    /**
     * @param {number} x
     * @param {number} y
     * @returns {GridTile}
     */
    findGridTileXY(x, y) {
        const tileCol = Fast.floor(x / Settings.tile.size);
        const tileRow = Fast.floor(y / Settings.tile.size);

        return this.getGridTile(tileCol, tileRow);
    }

    /**
     * @param {Vector} pixelLocation 
     * @returns {GridRegion}
     */
    findGridRegion(pixelLocation) {
        if (!this.groups) {
            return null;
        }
        const tile = this.findGridTile(pixelLocation);
        return tile != null ? tile.getRegion() : null;
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.changeCount = 0;
        this.shiftCount = 0;
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }
}